// ── Audio Engine (replaces WaveSurfer) ────────────────────
// Uses AudioBufferSourceNode for instant, gapless playback.
// Entire file decoded into memory for zero-latency seeking.

export class AudioEngine {
  ctx: AudioContext;
  buffer: AudioBuffer | null = null;
  source: AudioBufferSourceNode | null = null;
  gainNode: GainNode;
  private _playing = false;
  private _startTime = 0;    // AudioContext time when playback started
  private _offset = 0;       // offset into buffer in seconds
  private _duration = 0;
  onTimeUpdate: ((t: number) => void) | null = null;
  onFinish: (() => void) | null = null;
  private _rafId: number | null = null;

  constructor() {
    this.ctx = new AudioContext();
    this.gainNode = this.ctx.createGain();
    this.gainNode.connect(this.ctx.destination);
  }

  async load(url: string) {
    const resp = await fetch(url);
    const arrayBuf = await resp.arrayBuffer();
    this.buffer = await this.ctx.decodeAudioData(arrayBuf);
    this._duration = this.buffer.duration;
    this.stop();
  }

  async loadBlob(blob: Blob) {
    const arrayBuf = await blob.arrayBuffer();
    this.buffer = await this.ctx.decodeAudioData(arrayBuf);
    this._duration = this.buffer.duration;
    this.stop();
  }

  get duration() { return this._duration; }
  get isPlaying() { return this._playing; }

  get currentTime() {
    if (!this._playing) return this._offset;
    return this._offset + (this.ctx.currentTime - this._startTime);
  }

  play() {
    if (!this.buffer || this._playing) return;
    this.ctx.resume();
    this.source = this.ctx.createBufferSource();
    this.source.buffer = this.buffer;
    this.source.connect(this.gainNode);
    this.source.onended = () => {
      if (this._playing) {
        this._playing = false;
        this._offset = this._duration;
        this._stopRAF();
        this.onFinish?.();
      }
    };
    this.source.start(0, this._offset);
    this._startTime = this.ctx.currentTime;
    this._playing = true;
    this._startRAF();
  }

  pause() {
    if (!this._playing) return;
    this._offset = this.currentTime;
    this.source?.stop();
    this.source?.disconnect();
    this.source = null;
    this._playing = false;
    this._stopRAF();
  }

  playPause() {
    if (this._playing) this.pause();
    else this.play();
  }

  seek(time: number) {
    const wasPlaying = this._playing;
    if (wasPlaying) {
      this.source?.stop();
      this.source?.disconnect();
      this.source = null;
      this._playing = false;
    }
    this._offset = Math.max(0, Math.min(time, this._duration));
    if (wasPlaying) this.play();
  }

  stop() {
    this.pause();
    this._offset = 0;
  }

  // Get peaks for waveform rendering
  getPeaks(numBins: number): Float32Array {
    if (!this.buffer) return new Float32Array(numBins);
    const chan = this.buffer.getChannelData(0);
    const peaks = new Float32Array(numBins);
    const samplesPerBin = Math.floor(chan.length / numBins);
    for (let i = 0; i < numBins; i++) {
      let max = 0;
      const start = i * samplesPerBin;
      for (let j = start; j < start + samplesPerBin && j < chan.length; j++) {
        const abs = Math.abs(chan[j]);
        if (abs > max) max = abs;
      }
      peaks[i] = max;
    }
    return peaks;
  }

  // For filter chain access
  getOutputNode(): GainNode { return this.gainNode; }

  destroy() {
    this.stop();
    this._stopRAF();
    this.ctx.close();
  }

  private _startRAF() {
    const tick = () => {
      if (!this._playing) return;
      this.onTimeUpdate?.(this.currentTime);
      this._rafId = requestAnimationFrame(tick);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  private _stopRAF() {
    if (this._rafId != null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }
}
